import type { ColumnDef } from '@tanstack/react-table';
import { MessageSquareWarning } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HistorySection } from '@/features/audit-log';
import { useAuth, usePermissions } from '@/features/auth';
import { useSelectedCountry } from '@/features/country-scope';
import { ApiError } from '@/shared/api/api-error';
import { DataTable } from '@/shared/components/data-table/data-table';
import { DataTablePagination } from '@/shared/components/data-table/data-table-pagination';
import { useDataTable } from '@/shared/components/data-table/use-data-table';
import { EmptyState } from '@/shared/components/empty-state';
import { ErrorState } from '@/shared/components/error-state';
import { NotFoundPage } from '@/shared/components/errors/not-found-page';
import { PageContainer } from '@/shared/components/page-container';
import { PageHeader } from '@/shared/components/page-header';
import { useTableSearchParams } from '@/shared/hooks/use-table-search-params';
import { formatDateTime, formatRelativeDate } from '@/shared/lib/format';

import { useComplaint, useComplaints } from '../api/complaints.queries';
import { ComplaintActions } from '../components/complaint-actions';
import { ComplaintAssignmentCard } from '../components/complaint-assignment-card';
import { ComplaintConversation } from '../components/complaint-conversation';
import { ComplaintDetailsCard } from '../components/complaint-details-card';
import { ComplaintStateBanner } from '../components/complaint-state-banner';
import { ComplaintStatusBadge, NeedsReplyBadge } from '../components/complaint-status-badge';
import { complaintOutcome } from '../lib/complaint-outcome';
import type { ComplaintDetails, ComplaintSummary } from '../types/complaint.types';

const ANY = 'all';
const MINE = 'mine';
const UNASSIGNED = 'unassigned';

const STATUS_TABS = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
  { value: ANY, label: 'All' },
] as const;

const COLUMNS: ColumnDef<ComplaintSummary, unknown>[] = [
  {
    id: 'complaint',
    header: 'Complaint',
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex max-w-md min-w-48 flex-col gap-0.5">
        <Link
          to={`/complaints/${row.original.id}`}
          className="text-foreground font-medium hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          #{row.original.id} {row.original.complaintType ?? ''}
        </Link>
        {row.original.preview && (
          <span className="text-muted-foreground truncate text-xs" title={row.original.preview}>
            {row.original.preview}
          </span>
        )}
      </div>
    ),
  },
  {
    id: 'author',
    header: 'Customer',
    enableSorting: false,
    cell: ({ row }) => row.original.authorName ?? '—',
  },
  {
    id: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => (
      <span
        className="flex flex-wrap items-center gap-1.5"
        title={complaintOutcome(row.original) ?? undefined}
      >
        <ComplaintStatusBadge status={row.original.status} />
        {row.original.awaitingReply && <NeedsReplyBadge />}
        {row.original.resolution === 'refund' && (
          <span className="text-muted-foreground text-[11px]">with refund</span>
        )}
      </span>
    ),
  },
  {
    id: 'assignee',
    header: 'Owner',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.assigneeName ?? <span className="text-muted-foreground italic">Unassigned</span>,
  },
  {
    id: 'order',
    header: 'Order',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.orderId ? (
        <Link
          to={`/orders/${row.original.orderId}`}
          className="text-primary font-mono text-xs hover:underline"
          onClick={(event) => event.stopPropagation()}
        >
          #{row.original.orderId}
        </Link>
      ) : (
        '—'
      ),
  },
  {
    id: 'activity',
    header: 'Last activity',
    enableSorting: false,
    cell: ({ row }) => {
      const at = row.original.lastMessageAt ?? row.original.complaintDate;
      return (
        <div className="flex flex-col gap-0.5 text-xs">
          <span title={formatDateTime(at)}>{formatRelativeDate(at)}</span>
          <span className="text-muted-foreground text-[11px]">
            {row.original.messageCount === 1
              ? '1 message'
              : `${row.original.messageCount} messages`}
          </span>
        </div>
      );
    },
  },
];

/**
 * The read-only view (complaints.read without complaints.manage — Partner): the backend sends no
 * customer, owner or order, so their columns are left out rather than shown empty.
 */
const LIMITED_COLUMNS = COLUMNS.filter(
  (column) => column.id !== 'author' && column.id !== 'assignee' && column.id !== 'order',
);

/** Complaints on orders in the selected country, as a queue: open ones first by default. */
export function ComplaintsPage() {
  const tableState = useTableSearchParams();
  const navigate = useNavigate();
  const { country } = useSelectedCountry();
  const { user } = useAuth();
  const { can } = usePermissions();
  const limited = !can('complaints.manage');
  const status = tableState.getFilter('status') ?? 'open';
  const assigned = limited ? ANY : (tableState.getFilter('assigned') ?? ANY);

  const params = useMemo(
    () => ({
      page: tableState.page,
      pageSize: tableState.pageSize,
      status: status === ANY ? undefined : status,
      countryId: country?.id,
      assignedToUserId: assigned === MINE ? user?.id : undefined,
      unassigned: assigned === UNASSIGNED ? true : undefined,
    }),
    [tableState.page, tableState.pageSize, status, country?.id, assigned, user?.id],
  );
  const complaints = useComplaints(params);
  const table = useDataTable({
    data: complaints.data,
    columns: limited ? LIMITED_COLUMNS : COLUMNS,
    tableState,
    getRowId: (c) => String(c.id),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Complaints"
        description={
          limited
            ? 'Problems customers raised in your countries, and where each one stands. Read-only.'
            : 'Problems customers raised about their orders. Reply, take ownership, and close them here.'
        }
      />
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Tabs value={status} onValueChange={(value) => tableState.setFilter('status', value)}>
            <TabsList aria-label="Filter by status">
              {STATUS_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          {!limited && (
            <Select
              value={assigned}
              onValueChange={(value) => tableState.setFilter('assigned', value)}
            >
              <SelectTrigger size="sm" className="h-8 w-44 text-xs" aria-label="Filter by owner">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY} className="text-xs">
                  Any owner
                </SelectItem>
                <SelectItem value={MINE} className="text-xs">
                  Assigned to me
                </SelectItem>
                <SelectItem value={UNASSIGNED} className="text-xs">
                  Unassigned
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <DataTable
          table={table}
          isLoading={complaints.isLoading}
          isFetching={complaints.isFetching}
          error={complaints.error}
          onRetry={() => void complaints.refetch()}
          onRowClick={(complaint) => void navigate(`/complaints/${complaint.id}`)}
          emptyState={
            <EmptyState
              icon={MessageSquareWarning}
              title={
                status === 'open'
                  ? 'No open complaints'
                  : status === 'closed'
                    ? 'No closed complaints'
                    : 'No complaints'
              }
              description={
                status === 'open'
                  ? 'You are all caught up. New complaints from customers appear here.'
                  : 'Complaints raised on orders appear here.'
              }
            />
          }
        />
        <DataTablePagination table={table} />
      </div>
    </PageContainer>
  );
}

/**
 * One complaint: where it stands, the conversation with the customer, its
 * facts and owner beside it, and — with complaints.manage — how it ends.
 */
export function ComplaintPage() {
  const { complaintId } = useParams<{ complaintId: string }>();
  const parsedId = Number(complaintId);
  const isValidId = Number.isInteger(parsedId) && parsedId > 0;
  const complaint = useComplaint(isValidId ? parsedId : undefined, { live: true });
  const { can } = usePermissions();

  if (!isValidId) return <NotFoundPage />;
  if (complaint.isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-16" />
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <Skeleton className="h-96" />
          <Skeleton className="h-64" />
        </div>
      </PageContainer>
    );
  }
  if (
    complaint.error instanceof ApiError &&
    (complaint.error.isNotFound || complaint.error.isForbidden)
  ) {
    return <NotFoundPage />;
  }
  if (complaint.error || !complaint.data) {
    return (
      <PageContainer>
        <ErrorState error={complaint.error} onRetry={() => void complaint.refetch()} />
      </PageContainer>
    );
  }

  const data = complaint.data;
  const canManage = can('complaints.manage');

  if (!canManage) return <LimitedComplaintView data={data} />;

  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/complaints', label: 'Complaints' }}
        title={
          <span className="flex items-center gap-2.5">
            Complaint #{data.complaint.id}
            <ComplaintStatusBadge status={data.complaint.status} />
          </span>
        }
        description={`${data.complaint.complaintType ?? 'Complaint'} · from ${data.complaint.authorName ?? 'a customer'}`}
        actions={canManage ? <ComplaintActions data={data} /> : undefined}
      />

      <ComplaintStateBanner data={data} />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-6">
          <ComplaintConversation data={data} canReply={canManage} />
          <HistorySection
            entityType="Complaint"
            entityId={String(data.complaint.id)}
            description="Staff actions on this complaint: replies, owner changes, and how it was closed."
            collapsible
          />
        </div>
        <aside className="flex flex-col gap-4" aria-label="Complaint details">
          {canManage && <ComplaintAssignmentCard data={data} />}
          <ComplaintDetailsCard data={data} showOwner={!canManage} />
        </aside>
      </div>
    </PageContainer>
  );
}

/**
 * The read-only complaint (complaints.read without complaints.manage — Partner): what was
 * raised, where it stands and its History. The backend sends no customer, conversation,
 * order or refund for this view, and there is nothing to act on.
 */
function LimitedComplaintView({ data }: { data: ComplaintDetails }) {
  return (
    <PageContainer>
      <PageHeader
        backTo={{ to: '/complaints', label: 'Complaints' }}
        title={
          <span className="flex items-center gap-2.5">
            Complaint #{data.complaint.id}
            <ComplaintStatusBadge status={data.complaint.status} />
          </span>
        }
        description={complaintOutcome(data.complaint) ?? data.complaint.complaintType ?? undefined}
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-6">
          <Card>
            <CardHeader className="border-b">
              <CardTitle className="text-sm">Complaint</CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <p className="text-sm leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap">
                {data.details ?? data.complaint.preview ?? 'No description was given.'}
              </p>
            </CardContent>
          </Card>
          <HistorySection
            entityType="Complaint"
            entityId={String(data.complaint.id)}
            description="How this complaint has moved: owner changes, status changes and how it was closed."
            collapsible
          />
        </div>
        <aside className="flex flex-col gap-4" aria-label="Complaint details">
          <ComplaintDetailsCard data={data} limited />
        </aside>
      </div>
    </PageContainer>
  );
}
